/**
 * iClosed scheduler integration helpers.
 *
 * iClosed replaces the previous ChiliPiper booking flow. Booking happens through
 * an embedded scheduler (inline widget) rather than a programmatic submit API,
 * so the form pre-fills known lead details via URL parameters and lets the
 * invitee pick a time inside the embed.
 */

/** iClosed Discovery Call event URL — the scheduler embed target. */
export const ICLOSED_EVENT_URL = "https://app.iclosed.io/e/Gail/gail-discovery";

/**
 * Dedicated Discovery Call events per landing page. These point at separate
 * iClosed events so bookings can be tracked/attributed per vertical instead of
 * funneling every landing page into the shared discovery event above.
 */
export const ICLOSED_INSURANCE_EVENT_URL =
  "https://app.iclosed.io/e/Gail/discovery-call-insurance";
export const ICLOSED_INSURANCE_EVENT_TITLE = "Gail — Discovery Call — Insurance";

export const ICLOSED_COLLECTIONS_EVENT_URL =
  "https://app.iclosed.io/e/Gail/discovery-call-collections";
export const ICLOSED_COLLECTIONS_EVENT_TITLE =
  "Gail — Discovery Call — Collections";

/** GPT-for-insurance ad LP. Aliases the insurance event for now; PostHog
 *  `source` keeps attribution distinct. Repoint to a dedicated event later. */
export const ICLOSED_GPT_INSURANCE_EVENT_URL = ICLOSED_INSURANCE_EVENT_URL;
export const ICLOSED_GPT_INSURANCE_EVENT_TITLE =
  "Gail — Discovery Call — Insurance";

/** Form-first (2-step) variant loaded on phones. Aliases the calendar-with-form
 *  event until an event with "Show calendar with the form" OFF exists in iClosed. */
export const ICLOSED_GPT_INSURANCE_FORM_FIRST_EVENT_URL =
  ICLOSED_GPT_INSURANCE_EVENT_URL;

/** Async loader for the iClosed inline/popup widget. */
export const ICLOSED_WIDGET_SRC = "https://app.iclosed.io/assets/widget.js";

/** Stylesheet widget.js injects; preloaded so the skeleton styles paint ASAP. */
export const ICLOSED_WIDGET_CSS = "https://app.iclosed.io/assets/widget.css";

/** Accessible title for the embedded scheduler (product/brand name). */
export const ICLOSED_EVENT_TITLE = "Gail — Discovery Call";

/** Prefix shared by every `message` event iClosed posts to the parent window. */
export const ICLOSED_MESSAGE_PREFIX = "iclosed.";

/** `message` event `data.type` iClosed posts when a call is booked. */
export const ICLOSED_CALL_SCHEDULED_EVENT = "iclosed.call_scheduled";

/**
 * Type guard for any iClosed window `message` (e.g. `iclosed.view`,
 * `iclosed.potential`, `iclosed.call_scheduled`). Used to detect when the
 * embedded scheduler app has actually booted.
 */
export function isIClosedMessage(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { type?: unknown }).type === "string" &&
    (data as { type: string }).type.startsWith(ICLOSED_MESSAGE_PREFIX)
  );
}

/**
 * Prefill query-param keys for the Discovery Call event's custom questions.
 *
 * iClosed pre-fills a custom field when the URL param key equals that field's
 * configured "Identifier" (set in AI Scheduler → Events → Gail Discovery Call →
 * Invitee Questions). `company-name` is the identifier iClosed has configured
 * for the company question — verified end-to-end against the live scheduler.
 * (name/email/phone use the built-in iClosed params instead.)
 *
 * The qualification questions (number of employees, AI timeline, AI tools) are
 * intentionally collected natively inside the iClosed scheduler rather than
 * pre-filled from here: their select/multi-select values are brittle to mirror
 * (e.g. iClosed splits multi-selects on commas, so options whose labels contain
 * commas can't be pre-filled at all). Keeping our form lean to name/email/phone/
 * company avoids that maintenance burden while still skipping the contact step.
 */
export const ICLOSED_FIELD_IDS = {
  company: "company-name",
} as const;

export interface IClosedLead {
  name?: string;
  email?: string;
  phone?: string;
  /**
   * Extra iClosed question identifiers → values. Each key must match a custom
   * field identifier configured on the iClosed event for the value to pre-fill.
   * Blank values are omitted.
   */
  customFields?: Record<string, string | undefined>;
}

/**
 * Builds the iClosed scheduler URL with known lead details pre-filled. iClosed
 * reads `iclosedName`, `iclosedEmail`, and `iclosedPhone` for the primary
 * contact fields, plus any custom-question identifiers passed in `customFields`.
 * When the primary fields are present (and "pre-qualified form skipping" is
 * enabled on the event) the invitee skips the questionnaire and goes straight to
 * time selection. Blank fields are omitted.
 */
export function buildIClosedSchedulerUrl(
  lead: IClosedLead = {},
  baseUrl: string = ICLOSED_EVENT_URL,
): string {
  const url = new URL(baseUrl);
  const params: Array<[string, string]> = [...url.searchParams.entries()];

  const addParam = (key: string, value: string | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) {
      params.push([key, trimmed]);
    }
  };

  addParam("iclosedName", lead.name);
  addParam("iclosedEmail", lead.email);
  addParam("iclosedPhone", lead.phone);

  for (const [key, value] of Object.entries(lead.customFields ?? {})) {
    addParam(key, value);
  }

  // Build the query manually with encodeURIComponent so spaces become "%20".
  // URLSearchParams encodes spaces as "+", which iClosed drops verbatim into
  // text fields (e.g. a Company Name of "Acme Test" would show as "Acme+Test").
  // encodeURIComponent encodes a literal "+" as "%2B", so values like
  // "1,000+ Employees" or a "+1..." phone are preserved correctly.
  const query = params
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");

  const base = `${url.origin}${url.pathname}`;
  return query ? `${base}?${query}${url.hash}` : `${base}${url.hash}`;
}

/**
 * Type guard for the window `message` payload iClosed posts when a call is
 * booked. Used to fire our own conversion analytics from the inline widget.
 */
export function isIClosedCallScheduledMessage(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { type?: unknown }).type === ICLOSED_CALL_SCHEDULED_EVENT
  );
}
